import {useCommand} from '@synako/haligator';
import {HalitrekErrorHandler, Navigable} from '@synako/halitrek';
import {ConversationDesc} from 'api-types/ffconnect-hal';
import {ChangeEvent, RefObject, useRef, useState} from 'react';

// Limite de taille des fichiers : 5 Mo
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

// Formats de documents acceptes pour les pieces jointes
export const DOCUMENTS_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx';

// Formats d'images acceptes pour les pieces jointes
export const IMAGES_ACCEPT = 'image/*';

interface UseEnvoyerMessageAvecPiecesJointesOptions {
  onSuccess?: () => void;
  onError: HalitrekErrorHandler;
  onFichierTropVolumineux: (filename: string) => void;
}

interface UseEnvoyerMessageAvecPiecesJointesReturn {
  // State
  messageContent: string;
  setMessageContent: (content: string) => void;
  selectedFiles: File[];
  isSubmitting: boolean;
  peutEnvoyer: boolean;

  // Handlers
  handleFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  envoyerMessage: () => void;

  // Refs pour les inputs fichier
  documentInputRef: RefObject<HTMLInputElement | null>;
  imageInputRef: RefObject<HTMLInputElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;

  // Helper pour reset le textarea
  resetTextareaHeight: () => void;
}

/**
 * Hook pour gérer l'envoi de messages avec pièces jointes multiples.
 * Gère la sélection de fichiers, la validation de taille, et l'envoi.
 *
 * Tous les fichiers sont envoyés dans un seul message.
 */
export function useEnvoyerMessageAvecPiecesJointes(
  conversationNav: Navigable<ConversationDesc>,
  options: UseEnvoyerMessageAvecPiecesJointesOptions,
): UseEnvoyerMessageAvecPiecesJointesReturn {
  // State pour le message à envoyer
  const [messageContent, setMessageContent] = useState('');
  // State pour les fichiers sélectionnés (permet la sélection multiple)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Refs pour les inputs fichier
  const documentInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Commande pour envoyer un message
  const {submit: submitEnvoyerMessage, isSubmitting} = useCommand(
    conversationNav,
    'c:envoyer-message',
    {
      onSuccess: () => {
        options.onSuccess?.();
      },
      onError: options.onError,
    },
  );

  // Vérifie si on peut envoyer le message
  const peutEnvoyer =
    messageContent.trim().length > 0 || selectedFiles.length > 0;

  // Gérer la sélection de fichiers (multiple)
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const validFiles: File[] = [];
      for (const file of Array.from(files)) {
        if (file.size > MAX_FILE_SIZE) {
          options.onFichierTropVolumineux(file.name);
        } else {
          validFiles.push(file);
        }
      }
      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    // Reset l'input pour permettre de resélectionner le même fichier
    e.target.value = '';
  };

  // Supprimer un fichier de la liste
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper pour reset la hauteur du textarea
  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '';
    }
  };

  // Envoyer le message avec toutes les pièces jointes en une seule requête
  const envoyerMessage = () => {
    if (!peutEnvoyer) return;

    const texte = messageContent.trim();
    const formData = new FormData();
    formData.append('contenu', texte || ' ');

    // Ajouter tous les fichiers dans le même FormData
    for (const file of selectedFiles) {
      formData.append('pieces_jointes', file);
    }

    submitEnvoyerMessage(formData);

    // Réinitialiser le formulaire
    setMessageContent('');
    setSelectedFiles([]);
    resetTextareaHeight();
  };

  return {
    // State
    messageContent,
    setMessageContent,
    selectedFiles,
    isSubmitting,
    peutEnvoyer,

    // Handlers
    handleFileSelect,
    handleRemoveFile,
    envoyerMessage,

    // Refs
    documentInputRef,
    imageInputRef,
    textareaRef,

    // Helper
    resetTextareaHeight,
  };
}
